import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { sendOtp, verifyOtp, createPassword } from "../Redux/Slice/Login";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [step, setStep] = useState("email");
  const [emailId, setEmailId] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(60);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    let interval;
    if (step === "otp" && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // allow only digits
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < otp.length - 1) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleSendOtp = async () => {
    if (!emailId.trim()) {
      enqueueSnackbar("Please enter your Email ID", { variant: "warning" });
      return;
    }
    if (!isValidEmail(emailId)) {
      enqueueSnackbar("Please enter a valid Email ID", { variant: "warning" });
      return;
    }
    try {
      const res = await dispatch(sendOtp({ emailId })).unwrap();
      if (res?.status) {
        enqueueSnackbar("OTP sent successfully", { variant: "success" });
        setStep("otp");
        setTimer(60);
      } else {
        enqueueSnackbar(res?.message || "Failed to send OTP", {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "Something went wrong", {
        variant: "error",
      });
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.join("").length !== 6) {
      enqueueSnackbar("Please enter all 6 digits of the OTP", {
        variant: "warning",
      });
      return;
    }

    try {
      const res = await dispatch(
        verifyOtp({ emailId, otp: otp.join("") })
      ).unwrap();
      if (res?.status) {
        enqueueSnackbar("OTP Verified", { variant: "success" });
        setStep("reset");
      } else {
        enqueueSnackbar(res?.message || "Invalid OTP", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "OTP verification failed", {
        variant: "error",
      });
    }
  };

  const handleResetPassword = async () => {
    if (!password || !confirmPassword) {
      enqueueSnackbar("Please enter both password fields", {
        variant: "warning",
      });
      return;
    }

    if (password !== confirmPassword) {
      enqueueSnackbar("Passwords do not match", { variant: "error" });
      return;
    }

    if (password.length < 6) {
      enqueueSnackbar("Password must be at least 6 characters", {
        variant: "warning",
      });
      return;
    }

    try {
      const res = await dispatch(
        createPassword({ emailId, password, confirmPassword })
      ).unwrap();

      if (res?.status) {
        enqueueSnackbar("Password changed successfully", {
          variant: "success",
        });
        navigate("/");
      } else {
        enqueueSnackbar(res?.message || "Failed to reset password", {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "Something went wrong", {
        variant: "error",
      });
    }
  };

  return (
    <Box maxWidth={400} mx="auto" mt={8} px={2}>
      {step === "email" && (
        <>
          <Box textAlign="center" mb={2}>
            <EmailIcon fontSize="large" color="primary" />
          </Box>
          <Typography variant="h5" textAlign="center" mb={2}>
            Forgot Password
          </Typography>
          <TextField
            fullWidth
            label="Email ID"
            value={emailId}
            onChange={(e) => setEmailId(e.target.value)}
            size="small"
            margin="normal"
          />
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleSendOtp}
          >
            Send OTP
          </Button>
        </>
      )}

      {step === "otp" && (
        <>
          <Box textAlign="center" mb={2}>
            <LockIcon fontSize="large" color="primary" />
          </Box>
          <Typography variant="h6" textAlign="center">
            Enter OTP sent to your email
          </Typography>

          <Box display="flex" justifyContent="center" gap={2} my={2}>
            {otp.map((digit, i) => (
              <TextField
                key={i}
                id={`otp-${i}`}
                value={digit}
                onChange={(e) => handleOtpChange(i, e.target.value)}
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: "center", fontSize: "20px" },
                }}
                sx={{ width: 50 }}
                size="small"
              />
            ))}
          </Box>

          <Typography variant="caption" textAlign="center" display="block">
            {timer > 0 ? (
              <>
                Resend OTP in{" "}
                <strong>00:{timer.toString().padStart(2, "0")}</strong>
              </>
            ) : (
              <Button onClick={handleSendOtp}>Resend OTP</Button>
            )}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleVerifyOtp}
          >
            Verify OTP
          </Button>
        </>
      )}

      {step === "reset" && (
        <>
          <Box textAlign="center" mb={2}>
            <LockIcon fontSize="large" color="primary" />
          </Box>
          <Typography variant="h6" textAlign="center">
            Set New Password
          </Typography>
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="small"
            margin="normal"
          />
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            size="small"
            margin="normal"
          />
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleResetPassword}
          >
            Reset Password
          </Button>
        </>
      )}
    </Box>
  );
};

export default ForgotPassword;
